// Verification sms code/email is automatically sent when updating the profile (see useProfile) when a new email/mobile number
// is added OR when updating any field on the profile if the email or mobile number have not yet been verified.
// Code in this file deals with resending/validating code and updating verification state.
// It works with verification when adding a mobile phone when enabling text notifications, on the account page when changing/adding
// an email/mobile number, or if the user clicks one of the Verify buttons on the Account page.
import axios from '@fs/zion-axios'
import { i18n } from '@fs/zion-locale'
import { VerificationCodeInvalidError, VerificationCompleteError } from '../errors'
import withNoticeWarnings from '../services/withNoticeWarnings'
import cisAxiosConfig from '../cisAxiosConfig'

const verifyToken = (token) =>
  axios.get(`/service/ident/cis/cis-public-api/v4/token/${token}`, cisAxiosConfig).then((response) => {
    const warning = response?.headers?.warning
    if (warning) {
      if (warning.includes('102')) {
        throw new VerificationCodeInvalidError()
      } else {
        throw new Error(`verify sms code cis-public-api warning: ${warning}`)
      }
    }
  })

// https://sg31b0.familysearch.org/service/ident/cis/cis-public-api/resource_IdentityWebService.html#resource_IdentityWebService_resendEmailVerification_POST
const resendFieldVerification = async (field) =>
  withNoticeWarnings(
    axios.post(
      `/service/ident/cis/cis-public-api/v4/resend/${field}?lng=${
        i18n.language
      }&referrer=FamilySearch&ctx=${encodeURIComponent(window.location.href)}`,
      undefined,
      cisAxiosConfig
    )
  )

function useEmailVerification() {
  const resendEmailVerification = async () => {
    await resendFieldVerification('email')
  }

  return {
    resendEmailVerification,
  }
}

function useSmsVerification() {
  const resendSmsVerification = async () => {
    try {
      await resendFieldVerification('sms')
    } catch (error) {
      if (error?.response?.data?.message?.includes('error.verification.complete')) {
        throw new VerificationCompleteError()
      }
      throw error
    }
  }

  const verifySmsCode = async (token) => {
    await verifyToken(token)
    // Caller is responsible for refetching profile to pick up the updated verified timestamp
  }

  return {
    resendSmsVerification,
    verifySmsCode,
  }
}

export default function useVerification() {
  const { resendEmailVerification } = useEmailVerification()
  const { resendSmsVerification, verifySmsCode } = useSmsVerification()

  return {
    resendEmailVerification,
    resendSmsVerification,
    verifySmsCode,
  }
}
